import { LitElement, html, nothing, css } from 'lit';
import { property } from 'lit/decorators.js';
import { IxCardTestIds } from './IxCardTestIds.js';
import { IxCardEvents } from './IxCardEvents.js';

export class IxCard extends LitElement {
  static get styles() {
    return [
      css`
        * {
          box-sizing: border-box;
        }

        .card-container {
          width: 190px;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          padding: 8px 12px;
          cursor: pointer;
          border-radius: 4px;
          max-width: 210px;
          border: 1px solid var(--clr-outline, #0922411f);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .card-container.active {
          border: 2px solid var(--clr-on-surface-primary, #1456e0);
        }

        .card-text {
          display: flex;
          flex-direction: column;
        }

        .card-header {
          color: var(--clr-on-surface, #092241);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 168px;
        }

        .card-sub-text {
          color: var(--clr-on-surface, #092241);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 168px;
        }

        .dlr-text-default {
          font-family: var(--root-primary-font);
          font-size: var(--text-default-size);
          line-height: var(--text-default-line-height);
          letter-spacing: var(--text-default-letter-spacing);
          font-weight: var(--text-default-weight);
          -webkit-text-decoration: var(--text-default-decoration);
          text-decoration: var(--text-default-decoration);
          text-transform: var(--text-default-transform);
        }

        .dlr-text-small {
          font-family: var(--root-primary-font);
          font-size: var(--text-small-size);
          line-height: var(--text-small-line-height);
          letter-spacing: var(--text-small-letter-spacing);
          font-weight: var(--text-small-weight);
          -webkit-text-decoration: var(--text-small-decoration);
          text-decoration: var(--text-small-decoration);
          text-transform: var(--text-small-transform);
        }

        .font-bold {
          font-weight: 700;
        }
      `,
    ];
  }

  @property({ type: String }) itemId = '';

  @property({ type: String }) name = '';

  @property({ type: Array }) names: string[] = [];

  @property({ type: Boolean }) isActive = false;

  @property({ type: String }) type = 'roles';

  private get cardName() {
    if (this.names.length === 1) {
      return `${this.names[0]}`;
    }
    return `${this.names.length} ${this.type}`;
  }

  public dispatchCardToggleEvent() {
    this.dispatchEvent(
      new CustomEvent(IxCardEvents.ToggleCard, {
        detail: this.itemId,
        composed: true,
      }),
    );
  }

  dispatchTooltipShowEvent(e: Event, text: string) {
    this.dispatchEvent(
      new CustomEvent(IxCardEvents.ShowTooltip, {
        detail: {
          target: e.target,
          tooltip: html`<ix-simple-tooltip>${text}</ix-simple-tooltip>`,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  dispatchTooltipHideEvent(e: Event) {
    this.dispatchEvent(
      new CustomEvent(IxCardEvents.HideTooltip, {
        detail: {
          target: e.target,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  render() {
    return html`
      <div
        class=${`dlr-text-default font-bold card-container ${
          this.isActive ? 'active' : ''
        }`}
        @click=${this.dispatchCardToggleEvent}
        @keydown=${this.dispatchCardToggleEvent}
      >
        <div class="card-text">
          <div
            data-testid="${IxCardTestIds.ACCOUNT(this.itemId)}"
            class="dlr-text-default font-bold card-header"
            @mouseover=${(e: MouseEvent) =>
              this.dispatchTooltipShowEvent(e, this.name)}
            @mouseout=${this.dispatchTooltipHideEvent}
            @blur=${this.dispatchTooltipHideEvent}
            @focus=${(e: FocusEvent) =>
              this.dispatchTooltipShowEvent(e, this.cardName)}
          >
            ${this.name}
          </div>
          ${this.type === 'roles'
            ? html` <div
                data-testid="${IxCardTestIds.ROLE(this.itemId)}"
                class="dlr-text-small card-sub-text"
                @mouseover=${(e: MouseEvent) =>
                  this.dispatchTooltipShowEvent(e, this.cardName)}
                @mouseout=${this.dispatchTooltipHideEvent}
                @blur=${this.dispatchTooltipHideEvent}
                @focus=${(e: FocusEvent) =>
                  this.dispatchTooltipShowEvent(e, this.cardName)}
              >
                ${this.cardName}
              </div>`
            : nothing}
        </div>
      </div>
    `;
  }
}
